import Layout from '@components/layout'
import { collectionPageModuleQuery } from '@data/sanity/queries/page'
import { type SanityCollectionPage } from '@data/sanity/queries/types/page'
import { type SanitySiteFragment } from '@data/sanity/queries/types/site'
import { type Locale, getAllLocales } from '@lib/language'
import {
  type BasePageProps,
  getNonNullValues,
  getPagePropsFromContext,
  getPageSanityClient,
} from '@lib/page'
import { type CustomStaticPropsContext, getPageUrl } from '@lib/routes'
import { getSanityClient } from '@lib/sanity/client'
import {
  defaultModuleQueryParameters,
  getChildCollectionPaths,
  getCollectionPage,
} from '@lib/sanity/page'
import { getBreadcrumbListSchema } from '@lib/schema/breadcrumb'
import { getVideoObjectSchemas } from '@lib/schema/video'
import { getShopifyShopForPageWithCache } from '@lib/shopify/store-cache'
import Modules from '@modules/modules'
import { type GetStaticPathsResult, type GetStaticPropsResult } from 'next'
import dynamic from 'next/dynamic'
import { type ParsedUrlQuery } from 'querystring'
import { useMemo } from 'react'

const PreviewModules = dynamic(() => import('@modules/preview-modules'))

type CollectionChildPageProps = BasePageProps & {
  slug: string
  childSlug: string
  page: SanityCollectionPage
  site: SanitySiteFragment
}

type StaticCollectionChildPageParams = ParsedUrlQuery & {
  slug: string
  childSlug: string
}

const CollectionChildPage = ({
  draftMode,
  locale,
  slug,
  childSlug,
  page,
  site,
}: CollectionChildPageProps) => {
  const canonicalUrl = useMemo(() => {
    const domain = site.generalSettings.siteURL.replace(/\/+$/g, '')
    const pageUrl = getPageUrl(locale, 'shopPageChild', childSlug)

    return `${domain}${pageUrl}`
  }, [childSlug, locale, site])

  const schemas = useMemo(() => {
    const organizationUrl = site.schemaSettings?.organization?.url
    const shopPageUrl = getPageUrl(locale, 'shopPage')
    const collectionPageUrl = getPageUrl(locale, 'collection', slug)
    const childCollectionPageUrl = getPageUrl(locale, 'collectionChild', [
      slug,
      childSlug,
    ])
    const breadcrumbListSchema = getBreadcrumbListSchema([
      {
        title: page.shopPage?.seo?.metaTitle,
        url: `${organizationUrl}${shopPageUrl}`,
      },
      {
        title: page.seo?.metaTitle ?? page.title,
        url: `${organizationUrl}${collectionPageUrl}`,
      },
      {
        title: childSlug,
        url: `${organizationUrl}${childCollectionPageUrl}`,
      },
    ])
    const videoObjectSchemas = getVideoObjectSchemas(page)

    return [breadcrumbListSchema, ...videoObjectSchemas]
  }, [childSlug, locale, page, site, slug])

  return (
    <Layout
      page={page}
      site={site}
      canonicalUrl={canonicalUrl}
      schemas={schemas}
      draftMode={draftMode}
    >
      {!draftMode && (
        <Modules
          modules={page.modules}
          childSlug={childSlug}
          quickFilterBasePath={`/shop/${slug}`}
          quickFilterAllPath={`/shop/${slug}`}
        />
      )}
      {draftMode && (
        <PreviewModules
          modules={page.modules}
          query={collectionPageModuleQuery}
          params={{
            ...defaultModuleQueryParameters,
            locale,
            collectionSlug: slug,
          }}
          childSlug={childSlug}
          quickFilterBasePath={`/shop/${slug}`}
          quickFilterAllPath={`/shop/${slug}`}
        />
      )}
    </Layout>
  )
}

export async function getStaticProps(
  context: CustomStaticPropsContext<StaticCollectionChildPageParams>,
): Promise<GetStaticPropsResult<CollectionChildPageProps>> {
  const sanityClient = getPageSanityClient(!!context.draftMode)
  const slug = context.params?.slug ?? ''
  const childSlug = context.params?.childSlug ?? ''
  const collectionPage = await getCollectionPage(
    sanityClient,
    context.locale,
    slug,
  )

  if (!collectionPage.page) {
    return {
      notFound: true,
      revalidate: 600,
    }
  }

  const shopifyShop = await getShopifyShopForPageWithCache(context.locale)

  return {
    props: {
      ...getPagePropsFromContext(context),
      slug,
      childSlug,
      page: getNonNullValues(collectionPage.page),
      site: getNonNullValues(collectionPage.site),
      shopifyShop,
    },
    revalidate: 600,
  }
}

export async function getStaticPaths(): Promise<
  GetStaticPathsResult<StaticCollectionChildPageParams>
> {
  if (process.env.NEXT_PUBLIC_APP_ENV === 'preview') {
    return {
      paths: [],
      fallback: 'blocking',
    }
  }

  const sanityClient = getSanityClient()
  const allLocales = getAllLocales()
  const childCollectionPaths = await getChildCollectionPaths(
    sanityClient,
    allLocales,
  )

  const paths = childCollectionPaths
    .filter((childCollection) => {
      return (
        !!childCollection.slug.current &&
        allLocales.includes(childCollection.locale as Locale)
      )
    })
    .flatMap((childCollection) => {
      const parentCollections = childCollection.parentCollections ?? []

      return parentCollections.map((parentCollection) => {
        return {
          params: {
            slug: parentCollection.slug.current,
            childSlug: childCollection.slug.current,
          },
          locale: childCollection.locale,
        }
      })
    })

  return {
    paths,
    fallback: 'blocking',
  }
}

export default CollectionChildPage
